import React, { useCallback, useEffect, useState, startTransition } from "react";
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "components/mui";
import { getMailLists } from "api/canmail/CANmailAPI";
import { convertUserTimeToServerTime } from "libs/utils/timezone";
import {
  DROP_EDITOR_MEDIA_QUERY,
  EMAIL_ADDRESS_REGEX,
  MULTI_EMAIL_ADDRESS_REGEX,
} from "constants/canmail";
import { useNotificationProvider } from "hooks";

import { RecipientsList } from "./RecipientsList";
import ComposerFields from "./components/ComposerFields";
import EditorPane from "./components/EditorPane";
import AttachmentBar from "./components/AttachmentBar";
import ActionBar from "./components/ActionBar";
import EmailSenderModals from "./components/EmailSenderModals";

import { useEmailBody } from "./hooks/useEmailBody";
import { useFromAddress } from "./hooks/useFromAddress";
import { useSurveyInserter } from "./hooks/useSurveyInserter";
import { useTemplateActions } from "./hooks/useTemplateActions";
import { useAttachments } from "./hooks/useAttachments";
import { useScheduledEmailEdit } from "./hooks/useScheduledEmailEdit";
import { useEmailDraft } from "./hooks/useEmailDraft";

/**
 * The CANmail composer.
 *
 * This component is the **orchestrator**: it owns the small amount of state that
 * genuinely spans concerns (recipient, subject, reply-to, schedule date, modal
 * visibility) and wires the feature hooks together. Anything with its own lifecycle
 * lives in `./hooks`, and all markup lives in `./components`.
 *
 * The one place several hooks legitimately meet is `loadScheduledEmail`, which has to
 * populate the body, attachments, From address and schedule together.
 */
function EmailSender() {
  const theme = useTheme();
  const { notifyError, notifySuccess } = useNotificationProvider();

  // Controls button layout only (full-width stacking on small screens).
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Whether the viewport is wide enough for Unlayer.
  const canUseDropEditor = useMediaQuery(DROP_EDITOR_MEDIA_QUERY);

  // --- Cross-cutting composer state ------------------------------------------
  const [mailLists, setMailLists] = useState([]);
  const [selectedTo, setSelectedTo] = useState("");
  // Recipient source: an existing saved list, or an ad-hoc set built on the spot.
  const [recipientMode, setRecipientMode] = useState("list"); // "list" | "adhoc"
  const [adHocRecipients, setAdHocRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [scheduledDate, setScheduledDate] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  // --- Modal visibility -------------------------------------------------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [loadTemplateOpen, setLoadTemplateOpen] = useState(false);
  const [updateTemplateOpen, setUpdateTemplateOpen] = useState(false);
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [cancelEditsOpen, setCancelEditsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // --- Feature hooks ----------------------------------------------------------
  const body = useEmailBody(canUseDropEditor);
  const from = useFromAddress();
  const attachmentsApi = useAttachments();
  const schedule = useScheduledEmailEdit();

  const templates = useTemplateActions({
    getFinalHtml: body.getFinalHtml,
    mode: body.mode,
    textContent: body.textContent,
    loadDropDesign: body.loadDropDesign,
    loadTextContent: body.loadTextContent,
    hasUneditableDropContent: body.hasUneditableDropContent,
    isEmpty: body.isEmpty,
  });

  // Survey links are placed differently per editor: the Drop editor gets a real button
  // block, the text editor gets an appended paragraph.
  const insertSurveyLink = useCallback(
    (surveyLink) => {
      if (body.isDropEditor) {
        const inserted = body.insertSurveyButton("Take Survey", surveyLink);
        if (!inserted) {
          notifyError("Editor is not ready. Please wait a moment and try again.");
        }
        // DropEditor reports its own success via the notification bar.
        return inserted;
      }

      // Drop the trailing empty paragraph the editor leaves behind, then append. Matches
      // both the old ReactQuill (`<p><br></p>`) and TinyMCE (`<p>&nbsp;</p>`, bogus `<br>`)
      // shapes so no blank line is left before the survey link.
      const cleaned = (body.textContent || "").replace(
        /<p>(?:&nbsp;|\s|<br[^>]*>)*<\/p>\s*$/i,
        ""
      );
      body.setTextContent(
        `${cleaned}<p>Take Survey: <a href="${surveyLink}">${surveyLink}</a></p>`
      );
      notifySuccess("Survey link inserted successfully!");
      return true;
    },
    [body, notifyError, notifySuccess]
  );

  const survey = useSurveyInserter({ onInsertLink: insertSurveyLink });

  const draft = useEmailDraft({
    values: {
      isDropEditor: body.isDropEditor,
      selectedTo,
      subject,
      replyTo,
      emailPrefix: from.emailPrefix,
      emailBodyText: body.textContent,
      emailBodyDropJson: body.dropDesignJson,
      scheduledDate,
    },
    // Auto-save is suspended while editing an already-scheduled email.
    enabled: !schedule.editMode,
  });

  // --- Data loading -----------------------------------------------------------
  useEffect(() => {
    async function fetchMailLists() {
      try {
        setMailLists(await getMailLists());
      } catch (error) {
        console.error("[EmailSender] Failed to fetch mail lists:", error);
      }
    }
    fetchMailLists();
  }, []);

  // --- Reset ------------------------------------------------------------------
  const resetEmailSender = useCallback(() => {
    // React 18 batches these; startTransition keeps the UI responsive meanwhile.
    startTransition(() => {
      setSelectedTo("");
      setRecipientMode("list");
      setAdHocRecipients([]);
      setSubject("");
      setReplyTo("");
      setScheduledDate(null);
      body.reset();
      attachmentsApi.reset();
      schedule.reset();
      templates.clearTracking();
      survey.reset();
      from.reset();
      draft.clearDraft();
    });
  }, [body, attachmentsApi, schedule, templates, survey, from, draft]);

  /**
   * Load a scheduled email back into the composer for editing.
   *
   * Exposed on `window` is not needed — this is called from the sessionStorage handoff
   * below, which is how Email History's Scheduled tab hands a row over.
   */
  const loadScheduledEmail = useCallback(
    async (data) => {
      try {
        // Choose the editor from whichever content field is populated.
        let useDropEditor;
        if (data.content_drop && data.content_drop.trim() !== "") {
          useDropEditor = true;
        } else if (data.content_text && data.content_text.trim() !== "") {
          useDropEditor = false;
        } else {
          useDropEditor = true;
          console.warn(
            "[EmailSender] Both ContentText and ContentDrop are empty, defaulting to Drop Editor"
          );
        }

        // A scheduled email replaces the composer wholesale, so the other editor is
        // cleared and the Drop editor is remounted to guarantee it adopts the design.
        if (useDropEditor) {
          body.loadDropDesign(data.content_drop, {
            renderedHtml: data.email_contents,
            remount: true,
            clearText: true,
          });
        } else {
          body.loadTextContent(data.content_text, { clearDrop: true });
        }

        setSelectedTo(data.list_id);
        from.applyAddress(data.from_email);
        setReplyTo(data.reply_to_email || "");
        setSubject(data.subject);

        attachmentsApi.loadExisting(data.attachments || []);

        // schedule_date may be a UTC timestamp or an already-Calgary server-time string.
        const isUtcTimestamp =
          data.schedule_date.includes("Z") || data.schedule_date.includes(".");
        const calgaryTimeString = isUtcTimestamp
          ? convertUserTimeToServerTime(new Date(data.schedule_date))
          : data.schedule_date;

        setScheduledDate(calgaryTimeString);
        schedule.beginEdit(data.schedule_id);

        // The user is editing a scheduled email, not a template.
        templates.clearTracking();
      } catch (error) {
        console.error("[EmailSender] Error loading scheduled email:", error);
        notifyError("Failed to load scheduled email for editing. Please try again.");
      }
    },
    [body, from, attachmentsApi, schedule, templates, notifyError]
  );

  // On mount: either pick up a scheduled email handed over from Email History, or
  // restore a cached draft. Never both.
  useEffect(() => {
    const run = async () => {
      try {
        const handoff = sessionStorage.getItem("editScheduledEmail");
        if (handoff) {
          sessionStorage.removeItem("editScheduledEmail"); // Clear after reading
          await loadScheduledEmail(JSON.parse(handoff));
          return;
        }

        const cached = draft.loadDraft();
        if (!cached) return;

        startTransition(() => {
          setSelectedTo(cached.selectedTo || "");
          setSubject(cached.subject || "");
          setReplyTo(cached.replyTo || "");
          if (cached.emailPrefix) from.setEmailPrefix(cached.emailPrefix);
          setScheduledDate(cached.scheduledDate || null);

          // Mode is restored regardless of whether there was body content to go with it.
          body.setIsDropEditor(cached.isDropEditor);

          if (cached.isDropEditor && cached.emailBodyDropJson) {
            body.loadDropDesign(cached.emailBodyDropJson, { remount: true });
          } else if (!cached.isDropEditor && cached.emailBodyText) {
            body.loadTextContent(cached.emailBodyText);
          }
        });
        notifySuccess("Your previous email draft has been restored.");
      } catch (error) {
        console.error("[EmailSender] Error restoring composer state:", error);
        notifyError("Failed to load scheduled email for editing. Please try again.");
      }
    };

    run();
    // Mount only — the loaders are stable enough and re-running would clobber input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Actions ----------------------------------------------------------------
  // Switching recipient source clears the other side so a stale selection can't be
  // sent. Ad-hoc sends are immediate and list-less in v1, so scheduling is cleared too.
  const handleRecipientModeChange = useCallback((mode) => {
    setRecipientMode(mode);
    if (mode === "adhoc") {
      setSelectedTo("");
      setScheduledDate(null);
    } else {
      setAdHocRecipients([]);
    }
  }, []);

  const handleSendEmailClick = useCallback(
    async (isSendNow = false) => {
      if (body.hasUneditableDropContent) {
        notifyError(
          "This email was built in the Drop editor, which needs a wider screen. " +
            "Open it on a desktop to send it."
        );
        return;
      }
      const isAdHoc = recipientMode === "adhoc";
      if (isAdHoc) {
        if (adHocRecipients.length === 0) {
          notifyError("Add at least one recipient.");
          return;
        }
      } else if (!selectedTo) {
        notifyError("Please select a mailing list.");
        return;
      }
      if (!subject.trim()) {
        notifyError("Subject is required.");
        return;
      }

      // Export before validating, so unsaved Drop edits count as content and the body
      // that gets confirmed is the body that gets sent.
      const { html } = await body.getFinalHtml();
      if (body.isEmpty(html)) {
        notifyError("Email content is required.");
        return;
      }

      if (!EMAIL_ADDRESS_REGEX.test(from.emailFrom)) {
        notifyError("Please enter a valid email address in the 'From' field.");
        return;
      }
      if (replyTo && !MULTI_EMAIL_ADDRESS_REGEX.test(replyTo)) {
        notifyError(
          "Please enter valid email address(es) in the 'Reply-to' field. " +
            "For multiple addresses, use comma-separated format (e.g., 'support@example.com, sales@example.com')."
        );
        return;
      }

      const sizeError = attachmentsApi.validateSize();
      if (sizeError) {
        notifyError(sizeError);
        return;
      }

      schedule.setSendNowMode(isSendNow);
      setConfirmOpen(true);
    },
    [body, selectedTo, recipientMode, adHocRecipients, subject, from.emailFrom, replyTo, attachmentsApi, schedule, notifyError]
  );

  // Test Send has the same stale-content exposure as a real send.
  const handleTestSendClick = useCallback(async () => {
    await body.getFinalHtml();
    setTestSendOpen(true);
  }, [body]);

  const handleConfirmUpdateTemplate = useCallback(async () => {
    const shouldClose = await templates.updateTemplate();
    if (shouldClose) setUpdateTemplateOpen(false);
  }, [templates]);

  const handleResetBody = useCallback(() => {
    body.reset();
    templates.clearTracking();
    draft.clearDraft();
  }, [body, templates, draft]);

  const handlePreviewAttachment = useCallback((attachment) => {
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  }, []);

  const handleManageAttachmentsClick = useCallback(() => {
    attachmentsApi.setModalMode(schedule.editMode ? "edit" : "create");
    setAttachmentOpen(true);
  }, [attachmentsApi, schedule.editMode]);

  return (
    <Box sx={{ flex: 1, p: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        Send Email
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4} sx={{ display: "flex" }}>
          <ComposerFields
            mailLists={mailLists}
            selectedTo={selectedTo}
            onSelectedToChange={setSelectedTo}
            recipientMode={recipientMode}
            onRecipientModeChange={handleRecipientModeChange}
            adHocRecipients={adHocRecipients}
            onAdHocRecipientsChange={setAdHocRecipients}
            emailPrefix={from.emailPrefix}
            domain={from.domain}
            isPrefixLocked={from.isPrefixLocked}
            onPrefixChange={from.changePrefix}
            onPrefixBlur={from.savePrefix}
            onTogglePrefixLock={from.togglePrefixLock}
            replyTo={replyTo}
            onReplyToChange={setReplyTo}
            subject={subject}
            onSubjectChange={setSubject}
            isDropEditor={body.isDropEditor}
            canUseDropEditor={body.canUseDropEditor}
            onEditorModeChange={body.setIsDropEditor}
            hasUneditableDropContent={body.hasUneditableDropContent}
            onLoadTemplateClick={() => setLoadTemplateOpen(true)}
            survey={survey}
          />
        </Grid>

        {recipientMode === "list" && selectedTo && (
          <Grid item xs={12} md={6} lg={4} sx={{ display: "flex" }}>
            <RecipientsList listId={selectedTo} />
          </Grid>
        )}
      </Grid>

      <EditorPane body={body} onReset={handleResetBody} />

      <AttachmentBar
        attachments={attachmentsApi.attachments}
        editMode={schedule.editMode}
        onPreview={handlePreviewAttachment}
        onRemoveAt={attachmentsApi.removeAt}
      />

      <ActionBar
        isMobile={isMobile}
        editMode={schedule.editMode}
        scheduledDate={scheduledDate}
        selectedTo={selectedTo}
        recipientMode={recipientMode}
        subject={subject}
        loadedTemplateId={templates.loadedTemplateId}
        hasUneditableDropContent={body.hasUneditableDropContent}
        onUpdateTemplateClick={() => setUpdateTemplateOpen(true)}
        onSaveTemplateClick={() => setSaveTemplateOpen(true)}
        onManageAttachmentsClick={handleManageAttachmentsClick}
        onTestSendClick={handleTestSendClick}
        onScheduleClick={() => setScheduleOpen(true)}
        onCancelEditsClick={() => setCancelEditsOpen(true)}
        onSendNowClick={() => handleSendEmailClick(true)}
        onSendClick={() => handleSendEmailClick(false)}
        onClearSchedule={() => setScheduledDate(null)}
      />

      <EmailSenderModals
        modals={{
          confirm: confirmOpen,
          setConfirm: setConfirmOpen,
          saveTemplate: saveTemplateOpen,
          setSaveTemplate: setSaveTemplateOpen,
          loadTemplate: loadTemplateOpen,
          setLoadTemplate: setLoadTemplateOpen,
          updateTemplate: updateTemplateOpen,
          setUpdateTemplate: setUpdateTemplateOpen,
          attachment: attachmentOpen,
          setAttachment: setAttachmentOpen,
          testSend: testSendOpen,
          setTestSend: setTestSendOpen,
          schedule: scheduleOpen,
          setSchedule: setScheduleOpen,
          cancelEdits: cancelEditsOpen,
          setCancelEdits: setCancelEditsOpen,
          preview: previewOpen,
          setPreview: setPreviewOpen,
        }}
        body={body}
        attachmentsApi={attachmentsApi}
        schedule={schedule}
        templates={templates}
        composer={{
          mailLists,
          selectedTo,
          recipientMode,
          adHocRecipients,
          emailFrom: from.emailFrom,
          replyTo,
          subject,
          scheduledDate,
          previewAttachment,
          onEmailSent: resetEmailSender,
          onConfirmUpdateTemplate: handleConfirmUpdateTemplate,
          onScheduleSet: setScheduledDate,
          onUndoSchedule: () => setScheduledDate(null),
        }}
      />
    </Box>
  );
}

export default EmailSender;
